import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Gamepad2, 
  Trophy, 
  UserPlus,
  PlusCircle,
  Zap
} from 'lucide-react';

export function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-game-accent/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      {/* Main content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 text-center max-w-2xl mx-auto"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', bounce: 0.5, delay: 0.2 }}
          className="mb-8"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-game-accent to-pink-500 rounded-3xl shadow-2xl shadow-game-accent/40 animate-float">
            <Gamepad2 className="w-12 h-12 text-white" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-5xl md:text-7xl font-extrabold mb-4 bg-gradient-to-r from-white via-primary-200 to-game-accent bg-clip-text text-transparent"
        >
          صومعة ابراهيم للالعاب
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-xl text-white/70 mb-12"
        >
          العب العاب مع صحابك جامده جدا! 🎉
        </motion.p>

        {/* Game modes preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-2 gap-4 mb-12"
        >
          <div className="card text-center">
            <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">🕵️</span>
            </div>
            <h3 className="font-bold text-lg mb-1">لعبة الجاسوس</h3>
            <p className="text-white/60 text-sm">اكتشف الجاسوس بينكم!</p>
          </div>
          <div className="card text-center">
            <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">❓</span>
            </div>
            <h3 className="font-bold text-lg mb-1">لعبة الأسئلة</h3>
            <p className="text-white/60 text-sm">اختبر معلوماتك!</p>
          </div>
        </motion.div>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            onClick={() => navigate('/create')}
            className="btn-primary flex items-center justify-center gap-3 text-xl px-8 py-4"
          >
            <PlusCircle className="w-6 h-6" />
            إنشاء غرفة
          </button>
          <button
            onClick={() => navigate('/join')}
            className="btn-secondary flex items-center justify-center gap-3 text-xl px-8 py-4"
          >
            <UserPlus className="w-6 h-6" />
            انضم للعبة
          </button>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-16 grid grid-cols-3 gap-6"
        >
          <div className="text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-primary-400" />
            <p className="text-white/60 text-sm">2-10 لاعبين</p>
          </div>
          <div className="text-center">
            <Zap className="w-8 h-8 mx-auto mb-2 text-game-gold" />
            <p className="text-white/60 text-sm">لعب فوري</p>
          </div>
          <div className="text-center">
            <Trophy className="w-8 h-8 mx-auto mb-2 text-game-accent" />
            <p className="text-white/60 text-sm">نظام نقاط</p>
          </div>
        </motion.div>
      </motion.div>

      {/* Version */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 1 }}
        className="fixed bottom-4 text-sm text-white/30"
      >
        v1.0.0 • مبني على Cloudflare ⚡
      </motion.p>
    </div>
  );
}
